// All consent labels are server-rendered from xliff (UC v3's UC_UI.getSettingsLabels() returns {}).
// CookieDialog.head.html decides whether to show the dialog; CookieDialog.boot.html paints it
// from the SSR markup; this class installs the real handlers and drains queued clicks on
// UC_UI_INITIALIZED.

import LanguageDropdown from './CookieDialog.LanguageDropdown';

export default class CookieDialog {
    static init() {
        if (document.body.classList.contains('neos-backend')) return;
        Array.from(document.querySelectorAll('.cookie-dialog')).forEach(element => new CookieDialog(element));
    }

    constructor(element) {
        if (!element) return;
        this.element = element;
        this._closedByUs = false;
        this._openedFromShowMore = false;
        this.element.dataset.cookieDialogReady = 'true';

        // Consent is required — block ESC dismissal.
        this.element.addEventListener('cancel', (e) => e.preventDefault());

        // closedby="none" polyfill for Safari.
        if (!(typeof HTMLDialogElement !== 'undefined' && 'closedBy' in HTMLDialogElement.prototype)) {
            this.element.addEventListener('close', () => {
                if (!this._closedByUs) {
                    requestAnimationFrame(() => {
                        if (this.element.isConnected) {
                            try { this.element.showModal(); } catch (_) { /* already open */ }
                        }
                    });
                }
                this._closedByUs = false;
            });
        }

        // Reopen if the user backs out of UC's UI (first or second layer) without making a choice.
        this._onUsercentricsViewChanged = (e) => {
            try {
                const detail = e && e.detail;
                if (
                    this._openedFromShowMore
                    && window.UC_UI
                    && typeof window.UC_UI.isConsentRequired === 'function'
                    && window.UC_UI.isConsentRequired()
                    && detail
                    && detail.view === 'NONE'
                    && (detail.previousView === 'FIRST_LAYER' || detail.previousView === 'SECOND_LAYER')
                ) {
                    this._openedFromShowMore = false;
                    this.open();
                }
            } catch (err) {
                console.warn('[CookieDialog] UC_UI_VIEW_CHANGED', err);
            }
        };
        window.addEventListener('UC_UI_VIEW_CHANGED', this._onUsercentricsViewChanged);

        const acceptAllButton = this.element.querySelector('#cookie-dialog__accept-all');
        const denyAllButton = this.element.querySelector('#cookie-dialog__deny-all');
        const moreSettings = this.element.querySelector('#cookie-dialog__more-settings');
        if (acceptAllButton) acceptAllButton.addEventListener('click', () => this.acceptAll());
        if (denyAllButton)   denyAllButton.addEventListener('click', () => this.denyAll());
        if (moreSettings)    moreSettings.addEventListener('click', () => this.showMore());

        const dropdownRoot = this.element.querySelector('.language-dropdown');
        this.languageDropdown = new LanguageDropdown(dropdownRoot, {
            onLanguageSelect: (lang) => this.selectLanguage(lang),
        });

        // Reconcile with UC once it's initialised: open/close to match
        // isConsentRequired(), then drain clicks captured by the boot script.
        this._usercentricsUiReadyHandled = false;
        const onUsercentricsUiReady = () => {
            if (this._usercentricsUiReadyHandled) return;
            this._usercentricsUiReadyHandled = true;
            try {
                if (window.UC_UI && typeof window.UC_UI.isConsentRequired === 'function') {
                    if (window.UC_UI.isConsentRequired()) {
                        this.open();
                    } else if (this.element.open) {
                        this.close();
                    }
                }
                this._flushQueue();
            } catch (err) {
                console.warn('[CookieDialog] UC_UI_INITIALIZED handler', err);
                this.fallbackToNativeCmp('initialization');
            }
        };
        window.addEventListener('UC_UI_INITIALIZED', onUsercentricsUiReady);
        if (typeof window.UC_UI !== 'undefined' && typeof window.UC_UI.isConsentRequired === 'function') {
            onUsercentricsUiReady();
        }

        window.addEventListener('beforeunload', () => {
            try { this.element.close(); } catch (_) { /* ignore */ }
        });

        const styleguideOpenButton = document.querySelector('.cookie-dialog-styleguide__open');
        if (styleguideOpenButton) {
            styleguideOpenButton.addEventListener('click', () => this.open());
        }
    }

    _flushQueue() {
        const queue = window.__cookieDialogQueue;
        if (!Array.isArray(queue) || !queue.length) return;
        const items = queue.splice(0);
        items.forEach(item => {
            if (!item) return;
            if (item.type === 'language' && item.lang) {
                this.selectLanguage(item.lang);
                return;
            }
            if (item.type === 'button') {
                if (item.id === 'cookie-dialog__accept-all') this.acceptAll();
                else if (item.id === 'cookie-dialog__deny-all') this.denyAll();
                else if (item.id === 'cookie-dialog__more-settings') this.showMore();
            }
        });
    }

    fallbackToNativeCmp(reason) {
        this._openedFromShowMore = false;
        if (reason) console.warn('[CookieDialog] Falling back to Usercentrics CMP:', reason);
        try {
            if (window.UC_UI && typeof window.UC_UI.showSecondLayer === 'function') {
                window.UC_UI.showSecondLayer();
            }
        } catch (err) {
            console.warn('[CookieDialog] showSecondLayer failed', err);
        }
    }

    _callUC(method) {
        const fn = window.UC_UI && window.UC_UI[method];
        if (typeof fn !== 'function') return false;
        try {
            fn.call(window.UC_UI);
            return true;
        } catch (err) {
            console.warn('[CookieDialog] ' + method + ' threw', err);
            return false;
        }
    }

    _callUCOrQueue(method, queuedButtonId, fallbackReason) {
        this._deferUC(() => {
            if (this._callUC(method)) return;
            if (!this._usercentricsUiReadyHandled) {
                if (!Array.isArray(window.__cookieDialogQueue)) window.__cookieDialogQueue = [];
                window.__cookieDialogQueue.push({ type: 'button', id: queuedButtonId });
                return;
            }
            this.fallbackToNativeCmp(fallbackReason);
        });
    }

    // UC's acceptAllConsents / denyAllConsents / showSecondLayer / updateLanguage are heavy
    // synchronous calls — they fan out into consent state writes, view-change events, and the
    // GTM consent cascade. Defer that work past the next paint via setTimeout(0) so it doesn't
    // count toward INP; the dialog itself is closed through the dialog API before UC work starts.
    _deferUC(work) {
        setTimeout(work, 0);
    }

    acceptAll() {
        this.close();
        this._callUCOrQueue('acceptAllConsents', 'cookie-dialog__accept-all', 'acceptAllConsents unavailable');
    }

    denyAll() {
        this.close();
        this._callUCOrQueue('denyAllConsents', 'cookie-dialog__deny-all', 'denyAllConsents unavailable');
    }

    showMore() {
        this._deferUC(() => {
            if (this._callUC('showSecondLayer')) {
                this._openedFromShowMore = true;
                this.close();
            } else {
                this._openedFromShowMore = false;
                this.fallbackToNativeCmp('showSecondLayer unavailable');
            }
        });
    }

    selectLanguage(lang) {
        if (!lang) return;
        if (!window.UC_UI || typeof window.UC_UI.updateLanguage !== 'function') {
            this._deferUC(() => this.fallbackToNativeCmp('updateLanguage unavailable'));
            return;
        }
        this._deferUC(() => {
            let promise;
            try {
                promise = window.UC_UI.updateLanguage(lang);
            } catch (err) {
                console.warn('[CookieDialog] selectLanguage', err);
                this.fallbackToNativeCmp('updateLanguage threw');
                return;
            }
            this._openedFromShowMore = true;
            this.close();
            Promise.resolve(promise)
                .catch(() => {})
                .then(() => {
                    if (window.UC_UI && typeof window.UC_UI.showFirstLayer === 'function') {
                        window.UC_UI.showFirstLayer();
                    }
                });
        });
    }

    open() {
        // The head script may have set this class based on a localStorage guess
        // (consent already given). If UC later determines consent IS still required
        // and calls open(), the CSS hide rule must stop applying — strip the class
        // before showModal() so the dialog can actually appear.
        document.documentElement.classList.remove('uc-consented');

        // Already promoted via showModal — nothing to do.
        if (this.element.matches(':modal')) return;

        // SSR renders <dialog open> and the boot-fallback path uses setAttribute('open');
        // either leaves the dialog "open" but NOT in the top layer. Strip the attribute
        // so showModal() can properly promote it.
        if (this.element.hasAttribute('open')) {
            this.element.removeAttribute('open');
        }
        delete this.element.dataset.bootFallback;

        try {
            window.dispatchEvent(new CustomEvent('lock'));
            this._openedFromShowMore = false;
            this.element.showModal();
        } catch (err) {
            console.warn('[CookieDialog] open', err);
            window.dispatchEvent(new CustomEvent('unlock'));
            this.fallbackToNativeCmp('open threw');
        }
    }

    close() {
        try {
            this._closedByUs = true;
            if (this.element.matches(':modal') && !this.element.hasAttribute('open')) {
                this.element.setAttribute('open', '');
            }
            if (this.element.open || this.element.matches(':modal')) {
                this.element.close();
            } else {
                this.element.removeAttribute('open');
            }
        } catch (err) {
            this._closedByUs = false;
            console.warn('[CookieDialog] close', err);
            this.element.removeAttribute('open');
        }
        // Inert was applied at parse time by the boot script to trap focus while the dialog is
        // visible. Remove it whenever the dialog closes so the page can receive input again.
        var main = document.querySelector('main');
        if (main) main.removeAttribute('inert');
        window.dispatchEvent(new CustomEvent('unlock'));
    }
}
